/* import __COLOCATED_TEMPLATE__ from './custom-time-sla-side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import Delays from 'embercom/lib/workflows/helpers/delays';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type OfficeHoursSchedule from 'embercom/models/office-hours-schedule';
import type ConversationSla from 'embercom/models/inbox/conversation-sla';
import type { TaskGenerator } from 'ember-concurrency';

// SLA duration constants (in seconds)
const DEFAULT_MINIMUM_SLA_SECONDS = 30;
const TINY_MINIMUM_SLA_SECONDS = 9;
const SLA_NAME_MAX_LENGTH = 191;

interface Args {
  officeHoursSchedules: OfficeHoursSchedule[];
  conversationSlas: ConversationSla[];
  onClose: () => void;
  isOpen: boolean;
  onSlaCreated: (sla: ConversationSla) => void;
  sourceSla?: ConversationSla | null;
}
export default class CustomTimeSlaSideDrawer extends Component<Args> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @tracked showSideDrawer = false;
  @tracked slaName: string | null = '';
  @tracked firstReplyTimeInSeconds: number | null = null;
  @tracked nextReplyTimeInSeconds: number | null = null;
  @tracked resolutionTimeInSeconds: number | null = null;
  @tracked timeToCloseInSeconds: number | null = null;
  @tracked firstReplyDelayUnit = 'second';
  @tracked firstReplyDelayValue = 0;
  @tracked nextReplyDelayUnit = 'second';
  @tracked nextReplyDelayValue = 0;
  @tracked resolutionDelayUnit = 'second';
  @tracked resolutionDelayValue = 0;
  @tracked timeToCloseDelayUnit = 'second';
  @tracked timeToCloseDelayValue = 0;
  @tracked speedOfAnswerInSeconds = null;
  @tracked speedOfAnswerDelayUnit = 'second';
  @tracked speedOfAnswerDelayValue = 0;
  @tracked selectedScheduleId = this.defaultScheduleId;
  @tracked pauseOnWaitingOnCustomer = false;
  @tracked pauseOnSnooze = false;

  @action
  maybeSeedFromArgs() {
    this.clearForm();
    if (this.args.sourceSla) {
      this.seedFromSourceSla(this.args.sourceSla);
    }
  }

  private seedFromSourceSla(source: ConversationSla) {
    let delays = this.delays;

    let candidateName = this.intl.t('inbox.sla.custom-time-sla-drawer.clone-name-prefix', {
      name: source.name ?? '',
    });
    this.slaName = candidateName.slice(0, SLA_NAME_MAX_LENGTH);

    this.firstReplyTimeInSeconds = source.firstReplyTimeInSeconds ?? null;
    this.nextReplyTimeInSeconds = source.nextReplyTimeInSeconds ?? null;
    this.resolutionTimeInSeconds = source.resolutionTimeInSeconds ?? null;
    this.timeToCloseInSeconds = source.timeToCloseInSeconds ?? null;

    if (source.firstReplyTimeInSeconds) {
      this.firstReplyDelayUnit = delays.getDelayUnit(source.firstReplyTimeInSeconds);
      this.firstReplyDelayValue = delays.getDelayValue(source.firstReplyTimeInSeconds);
    }
    if (source.nextReplyTimeInSeconds) {
      this.nextReplyDelayUnit = delays.getDelayUnit(source.nextReplyTimeInSeconds);
      this.nextReplyDelayValue = delays.getDelayValue(source.nextReplyTimeInSeconds);
    }
    if (source.resolutionTimeInSeconds) {
      this.resolutionDelayUnit = delays.getDelayUnit(source.resolutionTimeInSeconds);
      this.resolutionDelayValue = delays.getDelayValue(source.resolutionTimeInSeconds);
    }
    if (source.timeToCloseInSeconds) {
      this.timeToCloseDelayUnit = delays.getDelayUnit(source.timeToCloseInSeconds);
      this.timeToCloseDelayValue = delays.getDelayValue(source.timeToCloseInSeconds);
    }

    this.selectedScheduleId = source.officeHoursScheduleId ?? this.defaultScheduleId;
    this.pauseOnWaitingOnCustomer = source.pauseOnWaitingOnCustomer ?? false;
    this.pauseOnSnooze = source.pauseOnSnooze ?? false;
  }

  get isCloning() {
    return Boolean(this.args.sourceSla);
  }

  get delays() {
    return new Delays();
  }

  get officeHoursSchedules() {
    return this.args.officeHoursSchedules;
  }

  get conversationSlas() {
    return this.args.conversationSlas;
  }

  onSlaCreated(sla: ConversationSla) {
    this.args.onSlaCreated(sla);
  }

  get trimmedSlaName() {
    let slaName = this.slaName || '';
    return slaName.trim();
  }

  get duplicatedName() {
    return this.conversationSlas
      .filter((sla) => !sla.isNew)
      .any((sla) => sla.name === this.trimmedSlaName);
  }

  get firstTimeValueNull() {
    return this.firstReplyTimeInSeconds === null;
  }

  get nextTimeValueNull() {
    return this.nextReplyTimeInSeconds === null;
  }

  get resolutionTimeValueNull() {
    return this.resolutionTimeInSeconds === null;
  }

  get timeToCloseValueNull() {
    return this.timeToCloseInSeconds === null;
  }

  get speedOfAnswerValueNull() {
    return this.speedOfAnswerInSeconds === null;
  }

  get speedOfAnswerFeatureEnabled() {
    return this.appService.app.canUseFeature('inbox-sla-speed-of-answer');
  }

  get minimumSlaSeconds() {
    // Use tiny minimum if feature flag is enabled, otherwise default minimum
    return this.appService.app.canUseFeature('tiny-minimum-sla-duration')
      ? TINY_MINIMUM_SLA_SECONDS
      : DEFAULT_MINIMUM_SLA_SECONDS;
  }

  get firstReplyTimeValid() {
    // we want this to be between minimum seconds and 100 days
    return (
      this.firstTimeValueNull ||
      (!this.firstTimeValueNull &&
        Number(this.firstReplyTimeInSeconds) >= this.minimumSlaSeconds &&
        Number(this.firstReplyTimeInSeconds) <= 8640000)
    );
  }

  get nextReplyTimeValid() {
    // we want this to be between minimum seconds and 100 days
    return (
      this.nextTimeValueNull ||
      (!this.nextTimeValueNull &&
        Number(this.nextReplyTimeInSeconds) >= this.minimumSlaSeconds &&
        Number(this.nextReplyTimeInSeconds) <= 8640000)
    );
  }

  get resolutionTimeValid() {
    // we want this to be between minimum seconds and 100 days
    return (
      this.resolutionTimeValueNull ||
      (!this.resolutionTimeValueNull &&
        Number(this.resolutionTimeInSeconds) >= this.minimumSlaSeconds &&
        Number(this.resolutionTimeInSeconds) <= 8640000)
    );
  }

  get timeToCloseValid() {
    // we want this to be between minimum seconds and 100 days

    return (
      this.timeToCloseValueNull ||
      (!this.timeToCloseValueNull &&
        Number(this.timeToCloseInSeconds) >= this.minimumSlaSeconds &&
        Number(this.timeToCloseInSeconds) <= 8640000 &&
        Number(this.timeToCloseInSeconds) >= Number(this.resolutionTimeInSeconds))
    );
  }

  get speedOfAnswerValid() {
    return (
      this.speedOfAnswerValueNull ||
      (!this.speedOfAnswerValueNull &&
        Number(this.speedOfAnswerInSeconds) >= this.minimumSlaSeconds &&
        Number(this.speedOfAnswerInSeconds) <= 8640000)
    );
  }

  get timeValidationError() {
    if (!this.firstReplyTimeValid || !this.nextReplyTimeValid) {
      // Use dynamic translation if feature flag is enabled, otherwise use static translation
      if (this.appService.app.canUseFeature('tiny-minimum-sla-duration')) {
        return this.intl.t('inbox.sla.custom-time-sla-drawer.invalid-response-times-dynamic', {
          minimumSeconds: this.minimumSlaSeconds,
        });
      } else {
        return this.intl.t('inbox.sla.custom-time-sla-drawer.invalid-response-times');
      }
    }

    if (!this.resolutionTimeValid || !this.timeToCloseValid) {
      // Use dynamic translation if feature flag is enabled, otherwise use static translation
      if (this.appService.app.canUseFeature('tiny-minimum-sla-duration')) {
        return this.intl.t('inbox.sla.custom-time-sla-drawer.invalid-completion-times-dynamic', {
          minimumSeconds: this.minimumSlaSeconds,
        });
      } else {
        return this.intl.t('inbox.sla.custom-time-sla-drawer.invalid-completion-times');
      }
    }

    if (!this.speedOfAnswerValid) {
      return this.intl.t('inbox.sla.custom-time-sla-drawer.invalid-speed-of-answer', {
        minimumSeconds: this.minimumSlaSeconds,
      });
    }
    return null;
  }

  get noValuesSelectedError() {
    return (
      this.firstTimeValueNull &&
      this.nextTimeValueNull &&
      this.resolutionTimeValueNull &&
      this.timeToCloseValueNull &&
      (!this.speedOfAnswerFeatureEnabled || this.speedOfAnswerValueNull)
    );
  }

  get saveButtonDisabled() {
    let invalidName = this.trimmedSlaName.length === 0 || this.duplicatedName;
    return invalidName || !!this.timeValidationError || this.noValuesSelectedError;
  }

  get defaultScheduleId(): string | undefined {
    let schedule = this.officeHoursSchedules.findBy('isDefault', true);
    if (schedule) {
      return schedule.id;
    } else if (this.officeHoursSchedules.length > 0) {
      return this.officeHoursSchedules.firstObject?.id;
    } else {
      return undefined;
    }
  }

  get appHasMoreThanOneOfficeHours() {
    return this.officeHoursSchedules.length > 1;
  }

  get dropdownSchedules() {
    return this.officeHoursSchedules.map((schedule) => {
      return {
        value: schedule.id,
        text: schedule.name,
        description: schedule.labelWithTimezone,
      };
    });
  }

  @task({ drop: true })
  *createNewSlaAndCloseDrawer(): TaskGenerator<void> {
    let sla = yield this.store
      .createRecord('inbox/conversation-sla', {
        name: this.trimmedSlaName,
        firstReplyTimeInSeconds: this.firstReplyTimeInSeconds,
        nextReplyTimeInSeconds: this.nextReplyTimeInSeconds,
        resolutionTimeInSeconds: this.resolutionTimeInSeconds,
        timeToCloseInSeconds: this.timeToCloseInSeconds,
        speedOfAnswerInSeconds: this.speedOfAnswerInSeconds,
        officeHoursScheduleId: this.selectedScheduleId,
        pauseOnWaitingOnCustomer: this.pauseOnWaitingOnCustomer,
        pauseOnSnooze: this.pauseOnSnooze,
      })
      .save();
    this.clearForm();
    this.closeCustomTimeSlaDrawer();
    this.onSlaCreated(sla);
  }

  @action
  clearForm() {
    this.slaName = '';
    this.firstReplyTimeInSeconds = null;
    this.nextReplyTimeInSeconds = null;
    this.resolutionTimeInSeconds = null;
    this.timeToCloseInSeconds = null;
    this.firstReplyDelayValue = 0;
    this.nextReplyDelayValue = 0;
    this.resolutionDelayValue = 0;
    this.timeToCloseDelayValue = 0;
    this.speedOfAnswerInSeconds = null;
    this.speedOfAnswerDelayValue = 0;
    this.speedOfAnswerDelayUnit = 'second';
    this.firstReplyDelayUnit = 'second';
    this.nextReplyDelayUnit = 'second';
    this.timeToCloseDelayUnit = 'second';
    this.resolutionDelayUnit = 'second';
    this.selectedScheduleId = this.defaultScheduleId;
    this.pauseOnWaitingOnCustomer = false;
    this.pauseOnSnooze = false;
  }

  @action
  closeCustomTimeSlaDrawer() {
    this.slaName = null;
    this.firstReplyTimeInSeconds = null;
    this.nextReplyTimeInSeconds = null;
    this.args.onClose();
  }

  @action
  setFirstReplyDelayUnit(unit: string) {
    this.firstReplyDelayUnit = unit;
    if (Number(this.firstReplyDelayValue) === 0) {
      this.firstReplyTimeInSeconds = null;
    } else {
      this.firstReplyTimeInSeconds = this.delays.getDelayInSeconds(this.firstReplyDelayValue, unit);
    }
  }

  @action
  setFirstReplyDelayValue() {
    if (Number(this.firstReplyDelayValue) === 0) {
      this.firstReplyTimeInSeconds = null;
    } else {
      this.firstReplyTimeInSeconds = this.delays.getDelayInSeconds(
        this.firstReplyDelayValue,
        this.firstReplyDelayUnit,
      );
    }
  }

  @action
  setNextReplyDelayUnit(unit: string) {
    this.nextReplyDelayUnit = unit;
    if (Number(this.nextReplyDelayValue) === 0) {
      this.nextReplyTimeInSeconds = null;
    } else {
      this.nextReplyTimeInSeconds = this.delays.getDelayInSeconds(this.nextReplyDelayValue, unit);
    }
  }

  @action
  setNextReplyDelayValue() {
    if (Number(this.nextReplyDelayValue) === 0) {
      this.nextReplyTimeInSeconds = null;
    } else {
      this.nextReplyTimeInSeconds = this.delays.getDelayInSeconds(
        this.nextReplyDelayValue,
        this.nextReplyDelayUnit,
      );
    }
  }

  @action
  setResolutionDelayUnit(unit: string) {
    this.resolutionDelayUnit = unit;
    if (Number(this.resolutionDelayValue) === 0) {
      this.resolutionTimeInSeconds = null;
    } else {
      this.resolutionTimeInSeconds = this.delays.getDelayInSeconds(this.resolutionDelayValue, unit);
    }
  }

  @action
  setResolutionDelayValue() {
    if (Number(this.resolutionDelayValue) === 0) {
      this.resolutionTimeInSeconds = null;
    } else {
      this.resolutionTimeInSeconds = this.delays.getDelayInSeconds(
        this.resolutionDelayValue,
        this.resolutionDelayUnit,
      );
    }
  }

  @action
  setTimeToCloseDelayUnit(unit: string) {
    this.timeToCloseDelayUnit = unit;
    if (Number(this.timeToCloseDelayValue) === 0) {
      this.timeToCloseInSeconds = null;
    } else {
      this.timeToCloseInSeconds = this.delays.getDelayInSeconds(this.timeToCloseDelayValue, unit);
    }
  }

  @action
  setTimeToCloseDelayValue() {
    if (Number(this.timeToCloseDelayValue) === 0) {
      this.timeToCloseInSeconds = null;
    } else {
      this.timeToCloseInSeconds = this.delays.getDelayInSeconds(
        this.timeToCloseDelayValue,
        this.timeToCloseDelayUnit,
      );
    }
  }

  @action
  setSpeedOfAnswerDelayUnit(unit: string) {
    this.speedOfAnswerDelayUnit = unit;
    if (Number(this.speedOfAnswerDelayValue) === 0) {
      this.speedOfAnswerInSeconds = null;
    } else {
      this.speedOfAnswerInSeconds = this.delays.getDelayInSeconds(
        this.speedOfAnswerDelayValue,
        unit,
      );
    }
  }

  @action
  setSpeedOfAnswerDelayValue() {
    if (Number(this.speedOfAnswerDelayValue) === 0) {
      this.speedOfAnswerInSeconds = null;
    } else {
      this.speedOfAnswerInSeconds = this.delays.getDelayInSeconds(
        this.speedOfAnswerDelayValue,
        this.speedOfAnswerDelayUnit,
      );
    }
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox::Slas::CustomTimeSlaSideDrawer': typeof CustomTimeSlaSideDrawer;
    'inbox/slas/custom-time-sla-side-drawer': typeof CustomTimeSlaSideDrawer;
  }
}
